import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {Badge} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {PaymentMethod} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible (§3z).
//
// ⚠️ CARD FIELDS SORT ON A FLATTENED COPY, NOT A DOTTED PATH.
// `sortRecords` reads `record[accessor]` and does not resolve `card.last4`, so
// a dotted accessor evaluates to undefined for every row — the header renders
// clickable and silently does nothing. `useCustomers`-style normalisation in
// usePaymentMethods copies the three sort keys up onto the record
// (`cardLast4` / `cardBrand` / `cardExpYear`); the cells still RENDER from
// `item.card`, so display and sort agree.
//
// `card` is absent for non-card methods, so those keys are undefined and the
// shared comparator puts those rows LAST in both directions — which is right:
// "no card" is a state, not a small value.
export const usePaymentMethodsTableColumns = () => {

    const columns: DataTableColumn<PaymentMethod>[] = [
        {
            accessor: 'type',
            sortable: true,
            title: I18n.get('Type'),
            width: '1fr',
            // Enum renders as a badge, not a raw string (§13).
            render: (item: PaymentMethod) => (
                <Badge variant='light'>{item.type}</Badge>
            ),
        },
        {
            accessor: 'cardLast4',
            sortable: true,
            title: I18n.get('Last 4'),
            width: '1fr',
            // Nullable fields render the em-dash placeholder, never blank or
            // `undefined` (§13). The old config used a hyphen '-'.
            render: (item: PaymentMethod) => item.card?.last4 ?? '—',
        },
        {
            accessor: 'cardBrand',
            sortable: true,
            title: I18n.get('Brand'),
            width: '1fr',
            render: (item: PaymentMethod) => item.card?.brand ?? '—',
        },
        {
            accessor: 'customerId',
            sortable: true,
            title: I18n.get('Customer'),
            width: '1fr',
            render: (item: PaymentMethod) => item.customerId ?? '—',
        },
        {
            // Sorts by EXPIRY YEAR, which is the meaningful order for a column
            // an operator scans for cards about to lapse. Sorting the rendered
            // "9/2027" string would put 10/2026 before 9/2026.
            accessor: 'cardExpYear',
            sortable: true,
            title: I18n.get('Expiry'),
            width: '1fr',
            render: (item: PaymentMethod) => item.card
                ? `${item.card.expMonth}/${item.card.expYear}`
                : '—',
        },
        {
            // Sorts on the normalised millisecond value so the ordering matches
            // what the cell displays. `created` (seconds) is still the source.
            accessor: 'createdAtMillis',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: PaymentMethod) => formatTimestamp(item.createdAtMillis),
        },
    ];

    return {columns};
};
